import * as React from 'react';
import { Component } from 'react';
import propz from 'propz';
import { AppUser } from 'Src/views/App/App';
import { AGE_GROUPS } from 'Src/consts/school';
import { getFullName, searchFunctionHighLevelTournamentEvents } from 'Src/helpers/autocomplete/autocomplete';
import { sortTournamentEventsByAge } from 'Src/helpers/event/event';
import { SchoolEvent } from 'Src/models/event';
import { IndividualTournamentTeamPlayer, TournamentTeamPlayerEvent } from 'Src/models/tournamentTeamPlayer';
import { SlotAssignmentModal, SlotAssignment } from '../SlotAssignmentModal';
import { FilterChipSelect, FilterChipOption } from 'Src/components/FilterChipSelect/FilterChipSelect';
import { SPORT_GENDER_SERVER_TO_CLIENT_MAPPING } from 'Src/consts/common';
import { SPORT_GENDER_TO_USER_GENDER_MAPPING } from 'Src/consts/user';
import { UnsavedChangesModal } from 'Src/components/UnsavedChangesModal/UnsavedChangesModal';
import './AddPlayerToEventForm.scss';

interface PlayerToReplace {
  playerId: string;
  firstName: string;
  lastName: string;
  replacedFirstName: string;
  replacedLastName: string;
}

interface SelectedEventItem {
  event: SchoolEvent;
  playerToReplace?: PlayerToReplace;
  student?: IndividualTournamentTeamPlayer;
  isExisting?: boolean;
}

interface Props {
  user: AppUser;
  students: IndividualTournamentTeamPlayer[];
  tournamentId: string;
  addPlayerToEvent: (
    assignments: {
      event: SchoolEvent;
      students: IndividualTournamentTeamPlayer[];
      playersToReplace: { eventId: string; playerId: string }[];
    }[]
  ) => void;
  onCloseClick: () => void;
}

interface State {
  availableEvents: SchoolEvent[];
  selectedEvents: SelectedEventItem[];
  selectedAvailableIds: string[];
  selectedAllocatedIds: string[];
  availableSearch: string;
  appliedAvailableSearch: string;
  allocatedSearch: string;
  appliedAllocatedSearch: string;
  isFilterOpen: boolean;
  sportFilters: string[];
  ageFilters: string[];
  genderFilters: string[];
  draftSportFilters: string[];
  draftAgeFilters: string[];
  draftGenderFilters: string[];
  isLoading: boolean;
  includeOlderAgeGroups: boolean;
  slotAssignmentEvent: SchoolEvent | null;
  slotAssignmentParticipants: any[];
  isDiscardWarningOpen: boolean;
}

type EligibilityLevel = 'green' | 'amber' | 'red';

interface ParticipationLimitWarning {
  studentId: string;
  studentName: string;
  eventType: 'individual' | 'relay';
  count: number;
  limit: number;
}

export class AddPlayerToEventForm extends Component<Props, State> {
  state: State = {
    availableEvents: [],
    selectedEvents: [],
    selectedAvailableIds: [],
    selectedAllocatedIds: [],
    availableSearch: '',
    appliedAvailableSearch: '',
    allocatedSearch: '',
    appliedAllocatedSearch: '',
    isFilterOpen: false,
    sportFilters: [],
    ageFilters: [],
    genderFilters: [],
    draftSportFilters: [],
    draftAgeFilters: [],
    draftGenderFilters: [],
    isLoading: true,
    includeOlderAgeGroups: false,
    slotAssignmentEvent: null,
    slotAssignmentParticipants: [],
    isDiscardWarningOpen: false
  };

  componentDidMount() {
    this.loadEvents();
  }

  loadEvents = (includeOlderAgeGroups = this.state.includeOlderAgeGroups) => {
    this.setState({ isLoading: true });
    this.searchEvents(includeOlderAgeGroups).then(availableEvents => {
      const savedEventsById = new Map<string, TournamentTeamPlayerEvent>();
      this.props.students.forEach(student =>
        (student.events || []).forEach(event => {
          if (event.id && !savedEventsById.has(event.id)) savedEventsById.set(event.id, event);
        })
      );
      const availableEventsById = new Map(availableEvents.map(event => [event.id, event]));
      const existingEventIds = new Set(savedEventsById.keys());
      const existingEvents: SelectedEventItem[] = Array.from(savedEventsById.entries()).map(
        ([eventId, savedEvent]) => ({
          event: availableEventsById.get(eventId) || (savedEvent as SchoolEvent),
          isExisting: true
        })
      );
      this.setState(prevState => ({
        availableEvents,
        selectedEvents: [
          ...existingEvents,
          ...prevState.selectedEvents.filter(item => !item.isExisting && !existingEventIds.has(item.event.id))
        ],
        isLoading: false,
        selectedAvailableIds: [],
        selectedAllocatedIds: []
      }));
    });
  };

  searchEvents = (includeOlderAgeGroups: boolean): Promise<SchoolEvent[]> => {
    const { user, students, tournamentId } = this.props;
    const ageGroupsNaming = propz.get(user, ['activeSchool', 'ageGroupsNaming'], '');
    return searchFunctionHighLevelTournamentEvents(user, students, tournamentId, includeOlderAgeGroups, [], '').then(
      events => {
        const studentAges = students.map(student => propz.get(student, ['form', 'age']));
        const oldestSelectedAge = Math.max(...studentAges);
        const ageGroupsCount = (AGE_GROUPS[ageGroupsNaming] || []).length;
        const allowedAges = includeOlderAgeGroups
          ? Array.from(
              { length: Math.max(0, ageGroupsCount - oldestSelectedAge) },
              (_, index) => oldestSelectedAge + index
            )
          : [oldestSelectedAge];
        return sortTournamentEventsByAge(
          events.filter(event => (event.ages || []).some(age => allowedAges.includes(age))),
          ageGroupsNaming
        );
      }
    );
  };

  onIncludeOlderAgeGroupsChange = () => {
    const includeOlderAgeGroups = !this.state.includeOlderAgeGroups;
    this.setState({ includeOlderAgeGroups }, () => this.loadEvents(includeOlderAgeGroups));
  };

  getEventName = (event: SchoolEvent): string => {
    const activeSchoolId = this.props.user.activeSchoolId;
    return (
      propz.get(event, ['generatedNames', activeSchoolId], '') ||
      propz.get(event, ['generatedNames', 'official'], '') ||
      propz.get(event, ['name'], 'Tournament event')
    );
  };

  getSportName = (event: SchoolEvent): string => propz.get(event, ['sport', 'name'], 'Sport');

  getAgeLabels = (event: SchoolEvent): string[] => {
    const naming = propz.get(this.props.user, ['activeSchool', 'ageGroupsNaming'], '');
    const names = AGE_GROUPS[naming] || [];
    return (event.ages || []).map(age => propz.get(names, [age, 'name'], propz.get(names, [age], `Y${age + 1}`)));
  };

  getSchoolPlayersInEvent = (event: SchoolEvent): any[] => {
    const { activeSchoolId, activeSchool } = this.props.user;
    const schoolIds: string[] = propz.get(activeSchool, ['schoolIds'], [activeSchoolId]);
    const belongsToSchool = player => schoolIds.includes(player.schoolId) || player.schoolId === activeSchoolId;
    const individuals = (event.individualsData || []).filter(belongsToSchool);
    if (individuals.length > 0) return individuals;
    const teamPlayers = (event.teamsData || [])
      .filter(belongsToSchool)
      .reduce((players: any[], team) => [...players, ...(team.players || [])], []);
    return teamPlayers.length > 0 ? teamPlayers : (event.players || []).filter(belongsToSchool);
  };

  getEligibility = (event: SchoolEvent): { level: EligibilityLevel; message: string } => {
    const maxPlayers = propz.get(event, ['sport', 'defaultLimits', 'maxPlayers']);
    if (typeof maxPlayers === 'number' && maxPlayers > 0 && this.getSchoolPlayersInEvent(event).length >= maxPlayers) {
      return { level: 'red', message: `Event is full (${maxPlayers}/${maxPlayers})` };
    }
    const mismatch = this.props.students.some(student => {
      const rawAge = propz.get(student, ['form', 'age']);
      const age = Number(rawAge);
      const eventAges = (event.ages || []).map(Number);
      const ageMismatch = Number.isFinite(age) && !eventAges.includes(age);
      const eventGender = String(event.gender || '');
      const expectedStudentGender = SPORT_GENDER_TO_USER_GENDER_MAPPING[eventGender];
      const genderMismatch = Boolean(expectedStudentGender) && expectedStudentGender !== student.gender;
      return ageMismatch || genderMismatch;
    });
    return mismatch
      ? { level: 'amber', message: 'Check age group or gender for selected players' }
      : { level: 'green', message: 'Eligible for all selected players' };
  };

  getFilteredAvailable = (): SchoolEvent[] => {
    const {
      availableEvents,
      selectedEvents,
      appliedAvailableSearch,
      sportFilters,
      ageFilters,
      genderFilters
    } = this.state;
    const selectedIds = selectedEvents.map(item => item.event.id);
    const alreadyAllocatedIds = this.props.students.reduce(
      (ids, student) => [...ids, ...(student.events || []).map(event => event.id)],
      [] as string[]
    );
    const query = appliedAvailableSearch.trim().toLowerCase();
    return availableEvents.filter(event => {
      const matchesSearch =
        !query || `${this.getEventName(event)} ${this.getSportName(event)}`.toLowerCase().includes(query);
      const matchesSport = sportFilters.length === 0 || sportFilters.includes(this.getSportName(event));
      const ages = this.getAgeLabels(event);
      const matchesAge = ageFilters.length === 0 || ageFilters.some(age => ages.includes(age));
      const gender = String(event.gender || 'Mixed');
      const matchesGender = genderFilters.length === 0 || genderFilters.includes(gender);
      return (
        !selectedIds.includes(event.id) &&
        !alreadyAllocatedIds.includes(event.id) &&
        matchesSearch &&
        matchesSport &&
        matchesAge &&
        matchesGender
      );
    });
  };

  getFilteredAllocated = (): SelectedEventItem[] => {
    const query = this.state.appliedAllocatedSearch.trim().toLowerCase();
    return this.state.selectedEvents.filter(
      item =>
        !query ||
        this.getEventName(item.event)
          .toLowerCase()
          .includes(query)
    );
  };

  toggleId = (field: 'selectedAvailableIds' | 'selectedAllocatedIds', id: string) => {
    this.setState(
      prevState =>
        ({
          [field]: prevState[field].includes(id)
            ? prevState[field].filter(value => value !== id)
            : [...prevState[field], id]
        } as Pick<State, typeof field>)
    );
  };

  addEvent = (event: SchoolEvent) => {
    const maxPlayers = propz.get(event, ['sport', 'defaultLimits', 'maxPlayers']);
    const schoolPlayers = this.getSchoolPlayersInEvent(event);
    if (
      typeof maxPlayers === 'number' &&
      maxPlayers > 0 &&
      schoolPlayers.length + this.props.students.length > maxPlayers
    ) {
      this.setState({ slotAssignmentEvent: event, slotAssignmentParticipants: schoolPlayers });
      return;
    }
    this.setState(prevState => ({
      selectedEvents: prevState.selectedEvents.some(item => item.event.id === event.id)
        ? prevState.selectedEvents
        : [...prevState.selectedEvents, { event }],
      selectedAvailableIds: prevState.selectedAvailableIds.filter(id => id !== event.id)
    }));
  };

  addSelected = () => {
    const events = this.state.availableEvents.filter(event => this.state.selectedAvailableIds.includes(event.id));
    events.forEach(this.addEvent);
  };

  removeSelected = () =>
    this.setState(prevState => ({
      selectedEvents: prevState.selectedEvents.filter(
        item => item.isExisting || !prevState.selectedAllocatedIds.includes(item.event.id)
      ),
      selectedAllocatedIds: []
    }));

  onSlotAssignmentConfirm = (assignments: SlotAssignment[]) => {
    const event = this.state.slotAssignmentEvent;
    if (!event) return;
    const newItems: SelectedEventItem[] = assignments.map(assignment => ({
      event,
      student: assignment.student,
      playerToReplace: assignment.replacingPlayerId
        ? {
            playerId: assignment.replacingPlayerId,
            firstName: assignment.student.firstName,
            lastName: assignment.student.lastName,
            replacedFirstName: assignment.replacedFirstName || '',
            replacedLastName: assignment.replacedLastName || ''
          }
        : undefined
    }));
    this.setState(prevState => ({
      selectedEvents: [...prevState.selectedEvents, ...newItems],
      selectedAvailableIds: prevState.selectedAvailableIds.filter(id => id !== event.id),
      slotAssignmentEvent: null,
      slotAssignmentParticipants: []
    }));
  };

  onSubmit = () => {
    if (this.getParticipationLimitWarnings().length > 0) return;

    const eventMap = new Map<
      string,
      {
        event: SchoolEvent;
        students: IndividualTournamentTeamPlayer[];
        playersToReplace: { eventId: string; playerId: string }[];
      }
    >();
    this.state.selectedEvents
      .filter(item => !item.isExisting)
      .forEach(item => {
        if (!eventMap.has(item.event.id)) {
          eventMap.set(item.event.id, { event: item.event, students: [], playersToReplace: [] });
        }
        const entry = eventMap.get(item.event.id)!;
        const students = item.student ? [item.student] : this.props.students;
        students.forEach(student => {
          if (!entry.students.some(existing => existing.userId === student.userId)) entry.students.push(student);
        });
        if (item.playerToReplace) {
          entry.playersToReplace.push({ eventId: item.event.id, playerId: item.playerToReplace.playerId });
        }
      });
    this.props.addPlayerToEvent(Array.from(eventMap.values()));
  };

  renderEventRow = (event: SchoolEvent, side: 'available' | 'allocated', isExisting = false) => {
    const eligibility = this.getEligibility(event);
    const selected =
      side === 'available'
        ? this.state.selectedAvailableIds.includes(event.id)
        : this.state.selectedAllocatedIds.includes(event.id);
    const maxPlayers = propz.get(event, ['sport', 'defaultLimits', 'maxPlayers']);
    return (
      <div
        className="eventAllocationForm__eventRow"
        key={event.id}
        onDoubleClick={() =>
          side === 'available'
            ? this.addEvent(event)
            : isExisting
            ? undefined
            : this.setState(prev => ({
                selectedEvents: prev.selectedEvents.filter(item => item.event.id !== event.id)
              }))
        }
      >
        <input
          type="checkbox"
          checked={selected}
          disabled={isExisting}
          onChange={() =>
            !isExisting &&
            this.toggleId(side === 'available' ? 'selectedAvailableIds' : 'selectedAllocatedIds', event.id)
          }
        />
        <span className={`eventAllocationForm__dot is-${eligibility.level}`} title={eligibility.message} />
        <div>
          <strong>{this.getEventName(event)}</strong>
          <span>
            {[
              this.getSportName(event),
              this.getAgeLabels(event).join(', '),
              event.gender,
              event.startTime
                ? new Date(event.startTime).toLocaleString('en-GB', {
                    day: '2-digit',
                    month: 'short',
                    year: 'numeric',
                    hour: '2-digit',
                    minute: '2-digit'
                  })
                : ''
            ]
              .filter(Boolean)
              .join(' · ')}
          </span>
          {typeof maxPlayers === 'number' && maxPlayers > 0 && (
            <span>
              {this.getSchoolPlayersInEvent(event).length}/{maxPlayers} allocated
            </span>
          )}
          {eligibility.level !== 'green' && (
            <em className={`is-${eligibility.level}`}>
              {eligibility.level === 'red' ? '⊘' : '△'} {eligibility.message}
            </em>
          )}
          {isExisting && <em>Already allocated</em>}
        </div>
        {side === 'allocated' && !isExisting && (
          <button
            type="button"
            aria-label={`Remove ${this.getEventName(event)}`}
            onClick={() =>
              this.setState(prev => ({
                selectedEvents: prev.selectedEvents.filter(item => item.event.id !== event.id)
              }))
            }
          >
            ×
          </button>
        )}
      </div>
    );
  };

  renderFilters = () => {
    const naming = propz.get(this.props.user, ['activeSchool', 'ageGroupsNaming'], '');
    const ageNames = AGE_GROUPS[naming] || [];
    const selectedStudentAges = this.props.students
      .map(student => Number(propz.get(student, ['form', 'age'])))
      .filter(Number.isFinite);
    const minimumAge = selectedStudentAges.length ? Math.max(...selectedStudentAges) : 0;
    const sports: FilterChipOption[] = Array.from(new Set(this.state.availableEvents.map(this.getSportName)))
      .sort()
      .map(value => ({ value, label: value }));
    const ages: FilterChipOption[] = Array.from(
      new Set(
        this.state.availableEvents.reduce(
          (all, event) => [...all, ...(event.ages || []).filter(age => age >= minimumAge)],
          [] as number[]
        )
      )
    )
      .sort((left, right) => left - right)
      .map(age => {
        const label = String(propz.get(ageNames, [age, 'name'], propz.get(ageNames, [age], `Y${age + 1}`)));
        return { value: label, label, order: age };
      });
    const genders: FilterChipOption[] = Array.from(
      new Set(this.state.availableEvents.map(event => String(event.gender || 'MIXED')))
    ).map(value => ({
      value,
      label: propz.get(SPORT_GENDER_SERVER_TO_CLIENT_MAPPING, [value], value.replace(/_/g, ' ').toLowerCase())
    }));
    return (
      <>
        <button
          type="button"
          className="eventAllocationForm__filterScrim"
          aria-label="Close filters"
          onClick={() => this.setState({ isFilterOpen: false })}
        />
        <form
          className="eventAllocationForm__filterDrawer"
          role="dialog"
          aria-modal="true"
          aria-label="Available events filters"
          onSubmit={event => {
            event.preventDefault();
            this.setState(state => ({
              sportFilters: state.draftSportFilters,
              ageFilters: state.draftAgeFilters,
              genderFilters: state.draftGenderFilters,
              isFilterOpen: false
            }));
          }}
        >
          <header>
            <strong>Filters</strong>
            <button type="button" aria-label="Close filters" onClick={() => this.setState({ isFilterOpen: false })}>
              ×
            </button>
          </header>
          <div className="eventAllocationForm__filterBody">
            <div className="eventAllocationForm__filterIntro">
              <strong>Refine available events</strong>
              <span>Select one or more values in each group. Filters are applied when you click Apply.</span>
            </div>
            <FilterChipSelect
              label="Sports"
              options={sports}
              selected={this.state.draftSportFilters}
              onToggle={value => this.toggleDraftFilter('draftSportFilters', value)}
              searchable
              searchPlaceholder="Search sports…"
              hint={<small>Search, then select sports to add them as filters.</small>}
            />
            <FilterChipSelect
              label="Age groups"
              options={ages}
              selected={this.state.draftAgeFilters}
              onToggle={value => this.toggleDraftFilter('draftAgeFilters', value)}
              hint={<small>Only the selected child’s age group and older groups are available.</small>}
            />
            <FilterChipSelect
              label="Gender"
              options={genders}
              selected={this.state.draftGenderFilters}
              onToggle={value => this.toggleDraftFilter('draftGenderFilters', value)}
            />
          </div>
          <footer>
            <button
              type="button"
              onClick={() => this.setState({ draftSportFilters: [], draftAgeFilters: [], draftGenderFilters: [] })}
            >
              Clear all
            </button>
            <div>
              <button type="button" onClick={() => this.setState({ isFilterOpen: false })}>
                Cancel
              </button>
              <button type="submit">Apply</button>
            </div>
          </footer>
        </form>
      </>
    );
  };

  getParticipationLimitWarnings = (): ParticipationLimitWarning[] => {
    const sourceEvent = this.state.selectedEvents.length
      ? this.state.selectedEvents[0].event
      : this.state.availableEvents[0];
    const tournament = propz.get<any>(sourceEvent, ['tournament'], null);
    if (!tournament || propz.get<any>(tournament, ['isEventParticipantLimitEnforced'], true) === false) return [];

    const individualLimit = Number(propz.get(tournament, ['maxIndividualEventsPerStudent'], 0));
    const relayLimit = Number(propz.get(tournament, ['maxRelayEventsPerStudent'], 0));

    return this.props.students.reduce((warnings, student) => {
      const individualEventIds = new Set<string>();
      const relayEventIds = new Set<string>();

      (student.events || []).forEach(event => (event.teamBased ? relayEventIds : individualEventIds).add(event.id));
      this.state.selectedEvents
        .filter(item => !item.isExisting)
        .forEach(item => {
          if (item.student && item.student.userId !== student.userId) return;
          const target = propz.get<boolean>(item.event, ['sport', 'isRelay'], false)
            ? relayEventIds
            : individualEventIds;
          target.add(item.event.id);
        });

      const studentName = getFullName(student);
      if (individualLimit > 0 && individualEventIds.size > individualLimit) {
        warnings.push({
          studentId: student.userId,
          studentName,
          eventType: 'individual',
          count: individualEventIds.size,
          limit: individualLimit
        });
      }
      if (relayLimit > 0 && relayEventIds.size > relayLimit) {
        warnings.push({
          studentId: student.userId,
          studentName,
          eventType: 'relay',
          count: relayEventIds.size,
          limit: relayLimit
        });
      }
      return warnings;
    }, [] as ParticipationLimitWarning[]);
  };

  toggleDraftFilter = (field: 'draftSportFilters' | 'draftAgeFilters' | 'draftGenderFilters', value: string) =>
    this.setState(
      prev =>
        ({
          [field]: prev[field].includes(value) ? prev[field].filter(item => item !== value) : [...prev[field], value]
        } as Pick<State, typeof field>)
    );

  requestClose = () => {
    if (this.state.selectedEvents.some(item => !item.isExisting)) this.setState({ isDiscardWarningOpen: true });
    else this.props.onCloseClick();
  };

  render() {
    const available = this.getFilteredAvailable();
    const allocated = this.getFilteredAllocated();
    const filterCount = this.state.sportFilters.length + this.state.ageFilters.length + this.state.genderFilters.length;
    const title =
      this.props.students.length === 1
        ? `Allocate to events — ${getFullName(this.props.students[0])}`
        : `Allocate to events — ${this.props.students.length} players`;
    const pendingAllocations = this.state.selectedEvents.filter(item => !item.isExisting);
    const participationLimitWarnings = this.getParticipationLimitWarnings();
    return (
      <div className="eventAllocationForm">
        <UnsavedChangesModal
          isOpen={this.state.isDiscardWarningOpen}
          message="You have unsaved event allocations. If you close now, those changes will be lost."
          onKeepEditing={() => this.setState({ isDiscardWarningOpen: false })}
          onDiscard={this.props.onCloseClick}
        />
        {this.state.slotAssignmentEvent && (
          <SlotAssignmentModal
            isOpen={true}
            event={this.state.slotAssignmentEvent}
            newStudents={this.props.students}
            currentParticipants={this.state.slotAssignmentParticipants}
            maxPlayers={propz.get(this.state.slotAssignmentEvent, ['sport', 'defaultLimits', 'maxPlayers'])}
            activeSchoolId={this.props.user.activeSchoolId}
            onConfirm={this.onSlotAssignmentConfirm}
            onClose={() => this.setState({ slotAssignmentEvent: null, slotAssignmentParticipants: [] })}
          />
        )}
        <header className="eventAllocationForm__head">
          <div>
            <h2>{title}</h2>
            <p>
              {this.props.students.length === 1
                ? 'Double-click an event to add it, or tick several and use “Add selected”.'
                : `Events added here will be allocated to all ${this.props.students.length} selected players.`}
            </p>
          </div>
          <button type="button" onClick={this.requestClose} aria-label="Close">
            ×
          </button>
        </header>
        {participationLimitWarnings.length > 0 && (
          <div className="eventAllocationForm__limitWarning" role="alert">
            <strong>Participation limit exceeded</strong>
            <p>You cannot save these allocations. Remove the extra event allocation(s) shown below.</p>
            <ul>
              {participationLimitWarnings.map(warning => (
                <li key={`${warning.studentId}-${warning.eventType}`}>
                  <strong>{warning.studentName}</strong> would be allocated to {warning.count} {warning.eventType}{' '}
                  events; this tournament allows no more than {warning.limit}.
                </li>
              ))}
            </ul>
          </div>
        )}
        <div className="eventAllocationForm__picker">
          <section className="eventAllocationForm__panel">
            <div className="eventAllocationForm__panelHead">
              <strong>Available events</strong>
              <span>{available.length} total</span>
            </div>
            <div className="eventAllocationForm__legend">
              <span>
                <i className="is-green" />
                Eligible
              </span>
              <span>
                <i className="is-amber" />
                Check age/gender
              </span>
              <span>
                <i className="is-red" />
                Full / ineligible
              </span>
            </div>
            <label className="eventAllocationForm__olderGroups">
              <input
                type="checkbox"
                checked={this.state.includeOlderAgeGroups}
                onChange={this.onIncludeOlderAgeGroupsChange}
              />
              <span>
                <strong>Include events for older age groups</strong>
                <small>Also show gender-compatible events above the selected players’ age group.</small>
              </span>
            </label>
            <div className="eventAllocationForm__searchRow">
              <label className="eventAllocationForm__searchField">
                <input
                  value={this.state.availableSearch}
                  onChange={event => this.setState({ availableSearch: event.target.value })}
                  onKeyDown={event => {
                    if (event.key === 'Enter') {
                      event.preventDefault();
                      this.setState({ appliedAvailableSearch: this.state.availableSearch });
                    }
                  }}
                  placeholder="Search events…"
                />
                {this.state.availableSearch && (
                  <button
                    type="button"
                    aria-label="Clear available events search"
                    onClick={() => this.setState({ availableSearch: '', appliedAvailableSearch: '' })}
                  >
                    ×
                  </button>
                )}
              </label>
              <button
                type="button"
                className={filterCount > 0 ? 'isActive' : ''}
                onClick={() =>
                  this.setState(state => ({
                    isFilterOpen: true,
                    draftSportFilters: [...state.sportFilters],
                    draftAgeFilters: [...state.ageFilters],
                    draftGenderFilters: [...state.genderFilters]
                  }))
                }
              >
                <svg viewBox="0 0 24 24" aria-hidden="true">
                  <path d="M4 6h16M7 12h10M10 18h4" />
                </svg>
                <span>Filters</span>
                {filterCount > 0 && <b>{filterCount}</b>}
              </button>
              {this.state.isFilterOpen && this.renderFilters()}
            </div>
            <div className="eventAllocationForm__listMeta">
              <input
                type="checkbox"
                checked={
                  available.length > 0 && available.every(event => this.state.selectedAvailableIds.includes(event.id))
                }
                onChange={event =>
                  this.setState({ selectedAvailableIds: event.target.checked ? available.map(item => item.id) : [] })
                }
              />{' '}
              {available.length} shown
            </div>
            <div className="eventAllocationForm__list">
              {this.state.isLoading ? (
                <p>Loading events…</p>
              ) : (
                available.map(event => this.renderEventRow(event, 'available'))
              )}
            </div>
          </section>
          <div className="eventAllocationForm__moves">
            <button
              type="button"
              disabled={!this.state.selectedAvailableIds.length}
              onClick={this.addSelected}
              aria-label="Add selected"
            >
              →
            </button>
            <button
              type="button"
              disabled={!this.state.selectedAllocatedIds.length}
              onClick={this.removeSelected}
              aria-label="Remove selected"
            >
              ←
            </button>
          </div>
          <section className="eventAllocationForm__panel isAllocated">
            <div className="eventAllocationForm__panelHead">
              <strong>Allocated</strong>
              <span>{this.state.selectedEvents.length} total</span>
            </div>
            <div className="eventAllocationForm__allocatedSearch">
              <label className="eventAllocationForm__searchField">
                <input
                  value={this.state.allocatedSearch}
                  onChange={event => this.setState({ allocatedSearch: event.target.value })}
                  onKeyDown={event => {
                    if (event.key === 'Enter') {
                      event.preventDefault();
                      this.setState({ appliedAllocatedSearch: this.state.allocatedSearch });
                    }
                  }}
                  placeholder="Search allocated…"
                />
                {this.state.allocatedSearch && (
                  <button
                    type="button"
                    aria-label="Clear allocated events search"
                    onClick={() => this.setState({ allocatedSearch: '', appliedAllocatedSearch: '' })}
                  >
                    ×
                  </button>
                )}
              </label>
            </div>
            <div className="eventAllocationForm__listMeta">
              <input
                type="checkbox"
                checked={
                  allocated.length > 0 &&
                  allocated.every(item => this.state.selectedAllocatedIds.includes(item.event.id))
                }
                onChange={event =>
                  this.setState({
                    selectedAllocatedIds: event.target.checked ? allocated.map(item => item.event.id) : []
                  })
                }
              />{' '}
              {allocated.length} shown
            </div>
            <div className="eventAllocationForm__list">
              {allocated.length ? (
                allocated.map(item => this.renderEventRow(item.event, 'allocated', item.isExisting))
              ) : (
                <p>No events allocated yet.</p>
              )}
            </div>
          </section>
        </div>
        <footer className="eventAllocationForm__foot">
          <span>
            {pendingAllocations.length} × {this.props.students.length} new player event allocation
          </span>
          <div>
            <button type="button" onClick={this.requestClose}>
              Cancel
            </button>
            <button
              type="button"
              className="isPrimary"
              disabled={!pendingAllocations.length || participationLimitWarnings.length > 0}
              onClick={this.onSubmit}
            >
              Save allocations
            </button>
          </div>
        </footer>
      </div>
    );
  }
}
